import React, { ReactNode, useMemo, useState } from "react";
import { Control, Controller, DeepMap, FieldError, RegisterOptions } from "react-hook-form";
import { fieldError } from "../../../../utils/functions";
import classNames from "classnames";
import { DatePicker } from "./DatePicker";
import { TimePicker, parseAmPmTime } from "./TimePicker";
import { format } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { DateValue } from "react-aria-components";
import { useTimezone } from "../../../../hooks/useTimezone";

type Transform<T> = {
  input: (value: T | null | undefined) => Date | string | null | undefined;
  output: (value: Date | string | null | undefined) => T | null | undefined;
};

type SharedDateTimePickerProps = {
  id?: string;
  timeWithRespectToAgencyTimezone?: boolean;
  readonly?: boolean;
  isDateUnavailable?: (date: DateValue) => boolean;
  UNSTABLE_portalContainer?: Element | undefined;
  label?: ReactNode;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  clearable?: boolean;
  readonlyFormat?: string;
  dateHeaderMode?: "single" | "default";
  forbidNullDate?: boolean;
  showErrors?: boolean;
  hourOffset?: number;
  portalErrorLabel?: boolean;
  yearless?: boolean;
  allowInvalidFormValues?: boolean;
  timePlaceholder?: string;
  dateLabel?: ReactNode;
  timeLabel?: ReactNode;
  transform?: Transform<any>;
  useClientTimezone?: boolean; // Selects the date in the client's timezone rather than UTC
  showInAgencyTime?: boolean;
};

type ControlledDateTimePickerProps<FormValue, Key extends string> = SharedDateTimePickerProps & {
  control: Control;
  name: string;
  defaultValue?: Date | string;
  errors?: DeepMap<Record<Key, FormValue>, FieldError>;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export function ControlledDateTimePicker<FormValue, Key extends string>(
  props: ControlledDateTimePickerProps<FormValue, Key>,
) {
  const {
    id,
    control,
    name,
    label,
    timeWithRespectToAgencyTimezone = true,
    readonly = false,
    errors,
    required = false,
    rules,
    className,
    labelClassName,
    clearable = false,
    defaultValue,
    readonlyFormat,
    dateHeaderMode,
    forbidNullDate,
    showErrors = false,
    hourOffset,
    portalErrorLabel = false,
    yearless = false,
    allowInvalidFormValues = false,
    timePlaceholder,
    dateLabel,
    timeLabel,
    useClientTimezone = false,
    showInAgencyTime,
    transform,
  } = props;

  const [validationError, setValidationError] = useState<string | null>(null);

  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={validationError ? { validate: () => validationError } : rules}
      render={({ onChange, value }) => {
        return (
          <DateTimePicker
            id={id}
            timeWithRespectToAgencyTimezone={timeWithRespectToAgencyTimezone}
            readonly={readonly}
            value={value}
            onChange={onChange}
            label={label}
            required={required}
            error={error?.content.message}
            UNSTABLE_portalContainer={props.UNSTABLE_portalContainer}
            isDateUnavailable={props.isDateUnavailable}
            className={className}
            clearable={clearable}
            readonlyFormat={readonlyFormat}
            dateHeaderMode={dateHeaderMode}
            forbidNullDate={forbidNullDate}
            onValidityChange={(msg) => {
              setValidationError(msg);
              // Wait for refresh cycle so rules are updated with the message before triggering validation
              setTimeout(() => {
                control.trigger(name);
              }, 0);
            }}
            showErrors={showErrors}
            hourOffset={hourOffset}
            portalErrorLabel={portalErrorLabel}
            labelClassName={labelClassName}
            yearless={yearless}
            allowInvalidFormValues={allowInvalidFormValues}
            timePlaceholder={timePlaceholder}
            dateLabel={dateLabel}
            timeLabel={timeLabel}
            useClientTimezone={useClientTimezone}
            showInAgencyTime={showInAgencyTime}
            transform={transform}
          />
        );
      }}
    />
  );
}

type DateTimePickerProps = SharedDateTimePickerProps & {
  readonly?: boolean;
  error?: string;
  value?: Date;
  onChange?: ((value?: Date | null) => void) | undefined;
  onValidityChange?: ((message: string | null) => void) | undefined;
};

class InvalidDate extends Date {
  readonly isInvalidDate = true;

  constructor(time?: Date | string | number) {
    super();
    if (time instanceof Date) {
      this.setTime(time.getTime());
    } else if (typeof time === "number") {
      this.setTime(time);
    } else if (time) {
      const [hours, minutes] = parseAmPmTime(time);
      this.setHours(hours, minutes, 0, 0);
    }
  }
}

export function isInvalidDate(date: Date | null | undefined): date is InvalidDate {
  return date instanceof InvalidDate;
}

function stringAsDate(dateStr: string | Date) {
  return typeof dateStr === "string" ? new Date(dateStr) : dateStr;
}

export const DateTimePicker = ({
  id,
  timeWithRespectToAgencyTimezone = true,
  readonly = false,
  isDateUnavailable,
  UNSTABLE_portalContainer,
  error,
  value,
  onChange,
  label,
  required = false,
  className,
  labelClassName,
  clearable = false,
  readonlyFormat,
  dateHeaderMode = "default",
  forbidNullDate = false,
  onValidityChange,
  showErrors = false,
  portalErrorLabel,
  hourOffset,
  yearless = false,
  allowInvalidFormValues,
  timePlaceholder,
  dateLabel,
  timeLabel,
  useClientTimezone = false,
  showInAgencyTime,
  transform = { input: (val) => val, output: (val) => val },
}: DateTimePickerProps) => {
  const transformedInput = transform.input(value);
  const timezone = useTimezone({ defaultToAgencyTimezone: timeWithRespectToAgencyTimezone });
  const targetTimezone = showInAgencyTime
    ? timezone.defaultTimezoneInfo.timezone
    : useClientTimezone
      ? timezone.clientTimezoneInfo.timezone
      : "UTC";

  const dateValue = useMemo(() => {
    if (!transformedInput) return null;
    const date = stringAsDate(transformedInput);
    const tzDate = new TZDate(date, targetTimezone);
    tzDate.setHours(0, 0, 0, 0);
    return tzDate;
  }, [transformedInput, targetTimezone]);

  const timeValue = useMemo(() => {
    if (!transformedInput) return null;
    const date = stringAsDate(transformedInput);
    const tzDate = new TZDate(date, targetTimezone);
    return format(tzDate, "h:mm a");
  }, [transformedInput, targetTimezone]);

  const combineDateAndTime = (date: Date | null | undefined, time: string | null): Date | null => {
    if (!date && !time) return null;

    // If time is provided but no date, use current date in the target timezone
    const effectiveDate = date || new TZDate(new Date(), targetTimezone);

    if (!time) {
      // Only date provided, set time to midnight in the target timezone
      const result = new TZDate(effectiveDate, targetTimezone);
      result.setHours(0, 0, 0, 0);
      return result;
    }

    // Both date and time provided
    const [hours, minutes] = parseAmPmTime(time);

    // Create a date in the target timezone with the specified time
    const result = new TZDate(effectiveDate, targetTimezone);
    result.setHours(hours, minutes, 0, 0);

    return result;
  };

  const handleDateChange = (newDate?: Date | null) => {
    const combined = combineDateAndTime(newDate, timeValue);

    if (!allowInvalidFormValues || newDate !== null) {
      onChange?.(transform.output(combined));
    } else if (!newDate && timeValue && combined) {
      const [hours, minutes] = parseAmPmTime(timeValue);
      const invalidDate = new InvalidDate();
      const tzDate = new TZDate(combined, targetTimezone);
      tzDate.setHours(hours, minutes, 0, 0);
      invalidDate.setTime(tzDate.getTime());
      onChange?.(transform.output(invalidDate));
    } else {
      onChange?.(transform.output(null));
    }

    if (newDate !== null) onValidityChange?.(null);
  };

  const handleTimeChange = (newTime: string | Date | undefined) => {
    const time = newTime instanceof Date ? format(new TZDate(newTime, targetTimezone), "h:mm a") : newTime || null;
    const combined = combineDateAndTime(dateValue, time);
    onChange?.(transform.output(combined));
  };

  const handleClear = () => {
    !forbidNullDate && onChange?.(transform.output(null));
    onValidityChange?.(null);
  };

  if (readonly) {
    const displayValue = transformedInput
      ? format(stringAsDate(transformedInput), readonlyFormat || "MM/dd/yyyy h:mm a")
      : "";
    return (
      <>
        {label && <label>{label}</label>}
        <span>{displayValue}</span>
      </>
    );
  }

  return (
    <div className={classNames(className, "tw-flex tw-flex-col")}>
      {label && (
        <label className={classNames("label-text tw-mb-2", labelClassName)}>
          {label}
          {required && <span className='tw-ml-1 tw-text-red'>*</span>}
        </label>
      )}

      <div className='tw-flex tw-gap-2'>
        <div className='tw-flex-1'>
          <DatePicker
            id={id ? `${id}-date` : undefined}
            timeWithRespectToAgencyTimezone={timeWithRespectToAgencyTimezone}
            readonly={readonly}
            value={dateValue || undefined}
            onChange={handleDateChange}
            error={error}
            portalErrorLabel={portalErrorLabel}
            label={dateLabel}
            isDateUnavailable={isDateUnavailable}
            UNSTABLE_portalContainer={UNSTABLE_portalContainer}
            clearable={clearable}
            headerMode={dateHeaderMode}
            forbidNullDate={forbidNullDate}
            showErrors={showErrors}
            hourOffset={hourOffset}
            useClientTimezone={useClientTimezone && !showInAgencyTime}
            useAgencyTimezone={showInAgencyTime}
            yearless={yearless}
            allowInvalidFormValues={allowInvalidFormValues}
          />
        </div>

        <div className='tw-flex-1'>
          <TimePicker
            id={id ? `${id}-time` : undefined}
            readonly={readonly}
            value={timeValue ?? undefined}
            error={error}
            onChange={handleTimeChange}
            label={timeLabel}
            clearable={clearable}
            placeholder={timePlaceholder}
            showTimezoneIfDifferent={showInAgencyTime}
          />
        </div>
      </div>

      {clearable && (dateValue || timeValue) && (
        <button
          type='button'
          onClick={handleClear}
          className='tw-mt-2 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary hover:tw-underline'
        >
          Clear date and time
        </button>
      )}
    </div>
  );
};
